/**
 * ExportMode — Export settings and code preview.
 * Ported from Builder-5 ExportMode.jsx with Zustand integration.
 */
import { useState, useMemo } from 'react';
import { useBuilderStore } from '@/state/store';
import { generateTokensCSS } from '@/generators/css/tokens';
import { generateComponentsCSS } from '@/generators/css/components';
import { generateMainJS } from '@/generators/js/main';
import { generateHTML } from '@/generators/html/page';
import { RenderIcon } from '@/components/preview/RenderIcon';

export function ExportMode() {
  const sidebarTab = useBuilderStore((s) => s.sidebarTab);
  const settings = useBuilderStore((s) => s.settings);
  const components = useBuilderStore((s) => s.components);
  const order = useBuilderStore((s) => s.order);
  const updateSettings = useBuilderStore((s) => s.updateSettings);
  const setOpenModal = useBuilderStore((s) => s.setOpenModal);
  const [codeTab, setCodeTab] = useState<'html' | 'css' | 'js'>('html');

  const generatedCode = useMemo(() => {
    const html = generateHTML(components, order, settings);
    const css = generateTokensCSS(settings) + '\n' + generateComponentsCSS();
    const js = generateMainJS(settings, components);
    return { html, css, js };
  }, [components, order, settings]);

  if (sidebarTab === 'code') {
    return (
      <>
        <div className="sidebar__section">
          <div className="code-tabs">
            {(['html', 'css', 'js'] as const).map((tab) => (
              <button
                key={tab}
                type="button"
                className={`code-tab ${codeTab === tab ? 'is-active' : ''}`}
                onClick={() => setCodeTab(tab)}
              >
                {tab.toUpperCase()}
              </button>
            ))}
          </div>
          <textarea
            className="code-preview"
            readOnly
            value={generatedCode[codeTab]}
          />
        </div>
      </>
    );
  }

  // Settings tab
  return (
    <>
      <div className="sidebar__section">
        <div className="sidebar__sectionHeader">
          <div className="sidebar__sectionTitle"><h3>Website-Info</h3></div>
        </div>
        <div className="sidebar__sectionBody">
          <div className="form-group">
            <label className="form-label">Site Name</label>
            <input
              type="text"
              className="form-input"
              value={settings.siteName || ''}
              onChange={(e) =>
                updateSettings({ ...settings, siteName: e.target.value })
              }
            />
          </div>
          <div className="form-group">
            <label className="form-label">Beschreibung</label>
            <textarea
              className="form-textarea"
              rows={3}
              value={settings.siteDescription || ''}
              onChange={(e) =>
                updateSettings({ ...settings, siteDescription: e.target.value })
              }
            />
          </div>
          <div className="form-group">
            <label className="form-label">Sprache</label>
            <select
              className="form-select"
              value={settings.language || 'de'}
              onChange={(e) =>
                updateSettings({ ...settings, language: e.target.value })
              }
            >
              <option value="de">Deutsch</option>
              <option value="en">English</option>
              <option value="fr">Fran\u00E7ais</option>
            </select>
          </div>
        </div>
      </div>

      <div className="sidebar__section">
        <div className="sidebar__sectionHeader">
          <div className="sidebar__sectionTitle"><h3>Unterseiten</h3></div>
        </div>
        <div className="sidebar__sectionBody">
          <label className="form-checkbox">
            <input
              type="checkbox"
              checked={settings.generateAngebot || false}
              onChange={() =>
                updateSettings({
                  ...settings,
                  generateAngebot: !settings.generateAngebot,
                })
              }
            />
            <span>Angebot-Seite generieren</span>
          </label>
          <label className="form-checkbox">
            <input
              type="checkbox"
              checked={settings.generateMethoden || false}
              onChange={() =>
                updateSettings({
                  ...settings,
                  generateMethoden: !settings.generateMethoden,
                })
              }
            />
            <span>Methoden-Seite generieren</span>
          </label>
          <label className="form-checkbox">
            <input
              type="checkbox"
              checked={settings.generateUeberMich || false}
              onChange={() =>
                updateSettings({
                  ...settings,
                  generateUeberMich: !settings.generateUeberMich,
                })
              }
            />
            <span>Über mich-Seite generieren</span>
          </label>
        </div>
      </div>

      <div className="sidebar__section">
        <div className="sidebar__sectionHeader">
          <div className="sidebar__sectionTitle"><h3>Rechtliches</h3></div>
        </div>
        <div className="sidebar__sectionBody">
          <label className="form-checkbox">
            <input
              type="checkbox"
              checked={settings.generateImpressum ?? true}
              onChange={() =>
                updateSettings({
                  ...settings,
                  generateImpressum: !(settings.generateImpressum ?? true),
                })
              }
            />
            <span>Impressum</span>
          </label>
          <label className="form-checkbox">
            <input
              type="checkbox"
              checked={settings.generateDatenschutz ?? true}
              onChange={() =>
                updateSettings({
                  ...settings,
                  generateDatenschutz: !(settings.generateDatenschutz ?? true),
                })
              }
            />
            <span>Datenschutz</span>
          </label>
          <label className="form-checkbox">
            <input
              type="checkbox"
              checked={settings.generateAgb || false}
              onChange={() =>
                updateSettings({
                  ...settings,
                  generateAgb: !settings.generateAgb,
                })
              }
            />
            <span>AGB</span>
          </label>
          <button
            type="button"
            className="sidebar__link"
            onClick={() => setOpenModal('settings')}
            style={{ marginTop: 8, fontSize: 12, color: 'var(--sb-text-muted)', cursor: 'pointer', background: 'none', border: 'none', textDecoration: 'underline', padding: 0 }}
          >
            Rechtliche Angaben bearbeiten →
          </button>
        </div>
      </div>

      <div className="sidebar__section">
        <div className="sidebar__sectionHeader">
          <div className="sidebar__sectionTitle"><h3>Tracking</h3></div>
        </div>
        <div className="sidebar__sectionBody">
          {[
            { value: 'none', label: 'Kein Tracking' },
            { value: 'ga4', label: 'Google Analytics 4' },
            { value: 'matomo', label: 'Matomo Self-Hosted' },
          ].map((opt) => (
            <label key={opt.value} className="form-checkbox">
              <input
                type="radio"
                name="trackingPresetExport"
                checked={(settings.trackingPreset || 'none') === opt.value}
                onChange={() =>
                  updateSettings({ ...settings, trackingPreset: opt.value })
                }
              />
              <span>{opt.label}</span>
            </label>
          ))}
          {(settings.trackingPreset === 'ga4') && (
            <div className="form-group" style={{ marginTop: 8 }}>
              <label className="form-label">GA4 Measurement ID</label>
              <input
                type="text"
                className="form-input"
                value={settings.tracking?.ga4MeasurementId || ''}
                onChange={(e) =>
                  updateSettings({
                    ...settings,
                    tracking: { ...settings.tracking, ga4MeasurementId: e.target.value },
                  })
                }
                placeholder="G-XXXXXXXXXX"
              />
            </div>
          )}
          {(settings.trackingPreset === 'matomo') && (
            <>
              <div className="form-group" style={{ marginTop: 8 }}>
                <label className="form-label">Matomo URL</label>
                <input
                  type="text"
                  className="form-input"
                  value={settings.tracking?.matomoUrl || ''}
                  onChange={(e) =>
                    updateSettings({
                      ...settings,
                      tracking: { ...settings.tracking, matomoUrl: e.target.value },
                    })
                  }
                  placeholder="https://analytics.example.com"
                />
              </div>
              <div className="form-group" style={{ marginTop: 4 }}>
                <label className="form-label">Site ID</label>
                <input
                  type="text"
                  className="form-input"
                  value={settings.tracking?.matomoSiteId || ''}
                  onChange={(e) =>
                    updateSettings({
                      ...settings,
                      tracking: { ...settings.tracking, matomoSiteId: e.target.value },
                    })
                  }
                  placeholder="1"
                />
              </div>
            </>
          )}
        </div>
      </div>

      <div className="sidebar__section">
        <div className="sidebar__sectionHeader">
          <div className="sidebar__sectionTitle"><h3>Exportieren</h3></div>
        </div>
        <div className="sidebar__sectionBody">
          <button
            type="button"
            className="sidebar__actionBtn"
            onClick={() => setOpenModal('export')}
          >
            <RenderIcon name="download" />
            <span>ZIP exportieren</span>
          </button>
        </div>
      </div>
    </>
  );
}
